/**
 * Accordion Component
 * Gère le comportement responsive et les animations d'ouverture/fermeture
 */
class AccordionComponent extends HTMLElement {
    constructor() {
        super();
        this.details = null;
        this.content = null;
        this.userInteracted = false;
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
    }

    connectedCallback() {
        this.details = this.querySelector('.c-accordion__item');
        this.summary = this.querySelector('.c-accordion__header');
        this.content = this.querySelector('.c-accordion__content');
        if (!this.details || !this.content || !this.summary) return;

        const isMobileOnly = this.hasAttribute('data-mobile-only');
        const openInMobile = this.hasAttribute('data-open-mobile');

        // Add animation on toggle - only listen to summary clicks
        this.summary.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleToggle();
        });

        if (isMobileOnly) {
            this.handleResponsive(openInMobile);

            // Track user interaction on mobile
            this.details.addEventListener('toggle', () => {
                if (window.innerWidth < 768) {
                    this.userInteracted = true;
                }
            });

            // Handle window resize
            this.resizeObserver = new ResizeObserver(() => {
                this.handleResponsive(openInMobile);
            });
            this.resizeObserver.observe(document.body);
        }
    }

    /**
     * Handle toggle with animation
     */
    handleToggle() {
        // Prevent multiple animations
        this.details.style.overflow = 'hidden';

        if (this.isClosing || !this.details.open) {
            this.open();
        } else if (this.isExpanding || this.details.open) {
            this.shrink();
        }
    }

    /**
     * Shrink the accordion content
     */
    shrink() {
        this.isClosing = true;

        const startHeight = `${this.content.offsetHeight}px`;
        const endHeight = '0px';

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.content.animate({
            height: [startHeight, endHeight],
            opacity: [1, 0],
            paddingTop: [getComputedStyle(this.content).paddingTop, '0px'],
            paddingBottom: [getComputedStyle(this.content).paddingBottom, '0px']
        }, {
            duration: 400,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
        });

        this.animation.onfinish = () => {
            this.details.removeAttribute('open');
            this.content.style.height = '';
            this.content.style.opacity = '';
            this.animation = null;
            this.isClosing = false;
            this.details.style.overflow = '';
        };

        this.animation.oncancel = () => {
            this.isClosing = false;
            this.content.style.height = '';
            this.content.style.opacity = '';
        };
    }

    /**
     * Expand the accordion content
     */
    open() {
        this.details.open = true;
        window.requestAnimationFrame(() => this.expand());
    }

    expand() {
        this.isExpanding = true;

        const startHeight = '0px';
        const endHeight = `${this.content.offsetHeight}px`;

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.content.animate({
            height: [startHeight, endHeight],
            opacity: [0, 1],
            paddingTop: ['0px', getComputedStyle(this.content).paddingTop],
            paddingBottom: ['0px', getComputedStyle(this.content).paddingBottom]
        }, {
            duration: 400,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
        });

        this.animation.onfinish = () => {
            this.content.style.height = '';
            this.content.style.opacity = '';
            this.animation = null;
            this.isExpanding = false;
            this.details.style.overflow = '';
        };

        this.animation.oncancel = () => {
            this.isExpanding = false;
            this.content.style.height = '';
            this.content.style.opacity = '';
        };
    }

    handleResponsive(openInMobile) {
        if (!this.details) return;

        if (window.innerWidth >= 768) {
            // Desktop: always open
            this.details.open = true;
        } else if (!this.userInteracted) {
            // Mobile: respect openInMobile setting only if user hasn't interacted
            this.details.open = openInMobile;
        }
    }

    disconnectedCallback() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
}

// Register the component
if (!customElements.get('accordion-component')) {
    customElements.define('accordion-component', AccordionComponent);
}